import { ModuleEnum, TimeEntryListRM } from '@herbjoy/apex-api-models'
import { IQService } from 'angular'
import { StateService } from 'angular-ui-router'
import moment from 'moment'
import { EmployeeService, PayrollService, TimeEntryService } from '../../../core/services'
import { PermissionGrantService } from '../../../core/services/data-services/permission-grant.service'
import { UtilityService } from '../../../core/services/utility.service'
import { TimeGapDecorator } from '../time-gap-decorator.service'
import templateUrl from './list-time-entries-container.component.html'


export const ListTimeEntriesContainerComponent = {
    templateUrl,
    controller: ListTimeEntriesContainerController,
    controllerAs: "vm",
    bindings: {
        startDate: "<?",
        endDate: "<?",
        icdCorporationID: "<?",
        employeeID: "<?",
        isReview: "<?",
        unapprovedOnly: "<?"
    }
}

interface TimeEntryListVM extends Omit<TimeEntryListRM, "NumberOfHours"> {
    TotalDuration?: number;
    StartTimeFormatted?: string;
    EndTimeFormatted?: string;
    NumberOfHours?: number | string;
}

function ListTimeEntriesContainerController($q: IQService, $uibModal: any, $state: StateService,
    employeeService: EmployeeService, utilityService: UtilityService, payrollService: PayrollService,
    payrollDashboardService, permissionGrantService: PermissionGrantService,
    timeGapDecorator: TimeGapDecorator, timeEntryService: TimeEntryService, apexApiUrl: string) {
    'ngInject'

    // eslint-disable-next-line @typescript-eslint/no-this-alias
    const vm = this;
    let defaultFilter;

    vm.$onInit = onInit;
    vm.filters;

    vm.onChangeFilters = onChangeFilters;
    vm.handleCurrDateChange = handleCurrDateChange;
    vm.onChangeIcdCorporation = onChangeIcdCorporation;
    vm.approve = approve;
    vm.approveAll = approveAll;
    vm.AddTimeEntry = addTimeEntry;
    vm.ExportSummary = exportSummary;
    vm.ExportPdf = exportPdf;
    vm.ExportAllSummary = exportAllSummary;
    vm.reviewerTimeAdd = reviewerTimeAdd;
    vm.editTimeEntry = editTimeEntry;
    vm.deleteTimeEntry = deleteTimeEntry;
    vm.onBackClicked = onBackClicked;
    vm.onChangePage = onChangePage;

    vm.employeeName;
    vm.disableTimeEntryModification = false;

    vm.timeEntries = new kendo.data.ObservableArray([]);
    vm.payrolls = [];

    function onInit() {
        vm.currDate = vm.endDate;
        vm.employeeId = vm.employeeID; // TODO: Merge all references to employeeID
        defaultFilter = { PageNumber: 1, PageSize: vm.employeeId ? null : 25 };
        restoreFilters();

        $q.when()
            .then(function () {
                utilityService.showLoading();
                return activate();
            })
            .catch(function (ex) {
                console.error(ex);
                alert("Initial load failed: " + utilityService.getExceptionMessage(ex));
            })
            .then(function () {
                vm.loaded = true;
                utilityService.hideLoading();
            });
    }

    function activate() {
        const timeEntryPermissionsLoaded = $q.all([employeeService.retrievePermissions({
            stateName: "timeEntry.reviewTime"
        }), permissionGrantService.retrievePermissionGrants({})])
            .then(([access, grants]) => {
                vm.permissions = {
                    ...access,
                    createSelfTimeEntry: grants.some(p => p.Description.Key === "action:createSelfTimeEntry"),
                    addTimeEntry: grants.some(p => p.Description.Key === "action:addTimeEntry"),
                    updateTimeEntry: grants.some(p => p.Description.Key === "action:updateTimeEntry"),
                    deleteTimeEntry: grants.some(p => p.Description.Key === "action:addTimeEntry"),
                    viewTimeSheet: grants.some(p => p.Description.Key === "action:viewTimeSheet"),
                }
            });
        const approverLoaded = employeeService.retrieveIdentity()
            .then(function (identity) {
                // Current user will be marked as the approver.
                vm.approver = identity;
                vm.approvingEmployee = identity.FirstName + " " + identity.LastName;
            });

        let employeeNameLoaded = $q.when();
        if (vm.employeeId) {
            employeeNameLoaded = employeeNameLoaded
                .then(function () {
                    return employeeService.retrieveEmployeeProfile(vm.employeeId);
                })
                .then(function (employee) {
                    vm.employeeName = employee.FirstName + " " + employee.LastName;
                });
        }

        const payrollsLoaded = loadPayrolls()
            .then(function () {
                vm.payrolls = vm.payrolls.sort(function (a, b) {
                    if (a.StartDate < b.StartDate) {
                        return 1;
                    } else if (a.StartDate > b.StartDate) {
                        return -1;
                    }
                    if (a.EndDate < b.EndDate) {
                        return 1;
                    }
                    if (a.EndDate < b.EndDate) {
                        return -1;
                    }
                    return 0;
                });
            })
            .then(function () {
                vm.selectedPayroll = selectDefaultPayroll(vm.payrolls, vm.startDate, vm.currDate);

                if (vm.selectedPayroll) {
                    vm.locked = !vm.payrolls.filter(function (payroll) {
                        // Get unlocked payrolls for this week. If none are returned, the whole page is locked.
                        return moment(payroll.StartDate).isSame(vm.selectedPayroll.StartDate, 'day') && !payroll.Locked;
                    }).length;
                }
            })
            .then(function () {
                if (!vm.employeeId) {
                    // Aggregate a corporation list from the payroll data of the selected rotation's payrolls.
                    return reduceRotationIcdCorporations(vm.selectedPayroll, null);
                }
            })
            .then(() => timeEntryPermissionsLoaded)
            .then(() => {
                return loadTimeEntryData().then(function (timeEntries) {
                    vm.timeEntries = timeEntries;
                });
            });

        return $q.all([payrollsLoaded, approverLoaded, employeeNameLoaded, timeEntryPermissionsLoaded]);
    }

    function loadPayrolls() {
        // When reviewing, the company dropdown will be available, so filter by that. Otherwise, get pay periods
        // belonging to the employee, which are company independent.
        let loadPayrolls;
        if (!vm.employeeId) {
            // Load payrolls by rotation and also allow company select.
            loadPayrolls = payrollDashboardService.retrievePayrollRotations()
                .then(function (rotations) {
                    return rotations.filter(function (rotation) {
                        return rotation.Payrolls.filter(function (payroll) {
                            return payroll.CanRead;
                        }).length > 0;
                    });
                })
                .then(function (rotations) {
                    rotations.forEach(function (rotation) {
                        // Rotation is locked when there are not any unlocked.
                        rotation.Locked = true;
                        rotation.CanRead = false;
                        rotation.CanUpdate = false;
                        rotation.Payrolls.reduce((rotation, payroll) => {
                            rotation.Locked = rotation.Locked && payroll.Locked;
                            rotation.CanRead = rotation.CanRead || payroll.CanRead;
                            rotation.CanUpdate = rotation.CanUpdate || payroll.CanUpdate;
                            return rotation;
                        }, rotation);
                    });
                    vm.payrolls = rotations;
                });
        } else {
            loadPayrolls = payrollService.retrievePayPeriods(vm.employeeId, ModuleEnum.TimeEntry)
                .then(function (payrolls) {
                    // This list can include multiple corporations, so perform a grouping.
                    vm.payrolls = payrolls.filter(function (payroll) {
                        return payroll.CanRead || !vm.isReview;
                    });
                    vm.icdCorporations = vm.payrolls.reduce(reducePayrollCorporations, [])
                        .sort(function (a, b) {
                            return a.ClientName < b.ClientName ? -1 : 1;
                        });
                    selectDefaultIcdCorporation();
                });
        }

        return loadPayrolls;
    }

    function selectDefaultPayroll(payrolls: any[], startDate, endDate) {
        // Return either the payroll with the given start and end date, or the
        // earliest payroll starting before the current date.

        //get ordered list
        const orderedPayrolls = Array.from(payrolls).sort(function (p, q) {
            return p.StartDate > q.StartDate ? -1 : 1;
        });

        if (startDate && endDate) {
            // If the payroll matches the start and end dates, select it.
            return orderedPayrolls.find(p => moment(p.StartDate).isSame(startDate, 'day')
                && moment(p.EndDate).isSame(endDate, 'day'));
        } else {

            //Get the last locked payroll
            const lastSubmittedPayroll = orderedPayrolls.filter(p => p.Locked).sort(function (p, q) {
                return p.StartDate > q.StartDate ? -1 : 1;
            })[0];

            //Get the first unlocked payroll that is after the last locked (if any)
            const firstUnsubmittedPayroll = orderedPayrolls.filter(function (payweek) {
                return !payweek.Locked && (!lastSubmittedPayroll || payweek.StartDate >= lastSubmittedPayroll.StartDate)
            }).sort(function (p, q) {
                return p.StartDate < q.StartDate ? -1 : 1;
            })[0];
            // Select the payroll with the given ID, or the first payroll in the list if none given.
            return firstUnsubmittedPayroll
                // Select the first pay week in the list.
                || orderedPayrolls[0];
        }
    }

    function reduceRotationIcdCorporations(rotation, icdCorporationID) {
        vm.icdCorporations = !rotation ? [] : rotation.Payrolls.reduce(reducePayrollCorporations, [])
            .sort(function (a, b) {
                return a.ClientName < b.ClientName ? -1 : 1;
            });
        selectDefaultIcdCorporation();
    }

    function loadTimeEntryData() {
        if (!vm.selectedPayroll) {
            // No payroll is selected, so no time entries can be retrieved.
            return $q.when([]);
        }
        let icdCorporationID;
        if (vm.selectedIcdCorporation) {
            icdCorporationID = vm.selectedIcdCorporation.Id;
        } else if (vm.employeeId) {
            // Payroll is a real payroll and not an aggregate of division rotations.
            icdCorporationID = vm.selectedPayroll.IcdCorporationID;
        }
        const query: any = !vm.selectedPayroll ? {} : {
            StartDate: vm.selectedPayroll.StartDate,
            EndDate: vm.selectedPayroll.EndDate,
            EmployeeID: vm.employeeId,
            IcdCorporationID: icdCorporationID
        };
        if (vm.filters) {
            query.EmployeeName = vm.filters.EmployeeGroup;
            query.Approved = vm.filters.Approved === undefined ? null : vm.filters.Approved;
            query.ApproverFullName = vm.filters.ApproverFullName;
            query.Date = vm.filters.Date;
            query.MealBreak = vm.filters.MealBreak;
            query.StartTime = vm.filters.StartTime;
            query.EndTime = vm.filters.EndTime;
            query.NumberOfHours = vm.filters.NumberOfHours;
            query.ProgramName = vm.filters.ProgramName;
            query.LocationName = vm.filters.LocationName;
            query.PageSize = vm.filters.PageSize;
            query.UnapprovedOnly = vm.unapprovedOnly;
        }

        // Disable time entry add/create/delete if the division disables it, the reviewer cannot update the payroll,
        // or the user cannot log their own time entries.
        vm.disableTimeEntryModification = vm.selectedPayroll.DivisionCountries.some(function (division) {
            return division.EnablePayrollDataSync;
        }) || !(vm.selectedPayroll.CanUpdate || !vm.isReview && vm.permissions.createSelfTimeEntry);

        vm.allPreApproved = true;
        return !query ? $q.when([]) :
            timeEntryService.retrieveTimeEntries(vm.filters.PageNumber - 1, query)
                .then(function (pagedEntityList) {
                    vm.timeEntryPage = pagedEntityList;
                    return pagedEntityList.Entities;
                })
                .then(function (data) {

                    let groups = []; // Initial value for groups.
                    groups = data.reduce(function (groups, timeEntry: TimeEntryListVM) {
                        const source = timeEntry as TimeEntryListRM;
                        // Loop through all time entries and insert the time entry into a group in the groups aggregate.
                        let employee = groups.filter(function (employee) {
                            // Find the employee group with the same employee ID.
                            return employee.EmployeeID === timeEntry.EmployeeID;
                        })[0];
                        if (!employee) {
                            // if the employee group doesn't exist yet, create it.
                            employee = {
                                EmployeeID: timeEntry.EmployeeID,
                                EmployeeFullName: timeEntry.EmployeeFullName,
                                TimeEntries: [],
                                IsSynced: false
                            };
                            employee.expand = true;
                            groups.push(employee);
                        }

                        const totalSeconds = Math.round(source.NumberOfHours * 3600); // Round to the nearest whole second.
                        const numSeconds = Math.floor(totalSeconds % 60);
                        const numMinutes = Math.floor((totalSeconds - numSeconds) / 60 % 60); // Should be strictly positive since Math.floor is used.
                        const numHours = Math.floor((totalSeconds - numSeconds - numMinutes * 60) / 3600);

                        timeEntry.TotalDuration = source.NumberOfHours; // Allow number of hours to still be accessed by value.
                        timeEntry.NumberOfHours = timeEntry.NumberOfHours !== 0
                            ? (numHours ? numHours + "h " : "") + (numMinutes !== 0 ? Math.floor(numMinutes) + "m" : "")
                            : null;

                        timeEntry.StartTimeFormatted = moment(timeEntry.StartTime, "HH:mm").format("h:mm a");
                        if (timeEntry.EndTime) {
                            timeEntry.EndTimeFormatted = moment(timeEntry.EndTime, "HH:mm").format("h:mm a");
                        }

                        if (!timeEntry.Approved) {
                            vm.allPreApproved = false;
                        }

                        if (timeEntry.IsSynced) {
                            employee.IsSynced = true;
                        }

                        employee.Locked = employee.Locked || timeEntry.Locked;

                        // insert the time entry into the group.
                        employee.TimeEntries.push(timeEntry);

                        return groups;
                    }, groups);

                    groups.forEach(function (group) {
                        setAllPreApproved(group);
                        group.TimeEntries.forEach(function (timeEntry) {
                            //If the entry is parent a entry and the duration is greater than 5 hours
                            //check for any assosicated break times
                            if (!timeEntry.ParentTimeEntryID && !timeEntry.IsBreak && timeEntry.TotalDuration > 5) {
                                const breakEntries = group.TimeEntries.filter(function (entry) {
                                    return entry.ParentTimeEntryID === timeEntry.TimeEntryID && entry.IsBreak
                                });
                                timeEntry.NoMealBreak = breakEntries.length === 0;
                            }
                            if (timeGapDecorator.isGapBetweenTimeEntries(timeEntry, group.TimeEntries, 1, "minutes")) {
                                timeGapDecorator.decorateTimeEntryWithGap(timeEntry, group.TimeEntries);
                            }
                            timeGapDecorator.decorateShow(timeEntry);
                        });
                    });

                    return groups;
                });
    }

    function handleCurrDateChange() {
        if (vm.selectedPayroll) {
            // Entire period is locked if all payrolls are locked.
            vm.locked = vm.selectedPayroll.Locked;

            $state.go(".", { startDate: vm.selectedPayroll.StartDate, endDate: vm.selectedPayroll.EndDate }, { inherit: true })
            utilityService.showLoading();
            return loadTimeEntryData().then(function (timeEntries) {
                vm.timeEntries = timeEntries;
            })
                .catch(function (ex) {
                    console.error(ex);
                    alert("Error changing date: " + utilityService.getExceptionMessage(ex));
                })
                .then(function () {
                    utilityService.hideLoading();
                });
        }
    }

    function onChangeIcdCorporation() {
        utilityService.showLoadingUntilResolved(
            utilityService.alertOnError(
                loadTimeEntryData().then(function (timeEntries) {
                    vm.timeEntries = timeEntries;
                })
                , "Changing company failed"));
    }

    function onChangeFilters() {
        changeFilters();
    }

    const changeFilters = utilityService.debounce(function () {
        vm.filters.PageNumber = 1;
        saveFilters();

        utilityService.showLoadingUntilResolved(
            utilityService.alertOnError(
                loadTimeEntryData().then(function (timeEntries) {
                    vm.timeEntries = timeEntries;
                }), "Filtering failed"));
    }, 500);

    function saveFilters() {
        localStorage.setItem("listTimeEntryFilters", JSON.stringify(vm.filters || {}));
    }

    function restoreFilters() {
        // Restore filters to the grid from localStorage, if available.
        vm.filters = JSON.parse(localStorage.getItem("listTimeEntryFilters")) || defaultFilter;
        vm.filters.PageNumber = 1;
        vm.filters.PageSize = vm.filters.PageSize || (vm.employeeId ? null : 25);
        // Boolean filters aren't restoring properly. TODO: Investigate and fix. For now, reset them to defaults.
        vm.filters.Approved = null;
        vm.filters.MealBreak = null;
        if (vm.filters.Date) {
            vm.filters.Date = moment(vm.filters.Date).toDate();
        }

        if (vm.filters == null) {
            vm.filters = { Approved: null };
            localStorage.setItem("listTimeEntryFilters", JSON.stringify(vm.filters));
        }
        return $q.when();
    }

    function approve(timeEntry) {
        if (timeEntry.Approved) {
            //Get all the time entries associated with this time entry
            const timeEntries = getAssociatedTimeEntries(timeEntry);
            const hasIncompleteTimeEntries = anyIncompletedTimeEntries(timeEntries);
            if (hasIncompleteTimeEntries) {
                timeEntry.Approved = false;
                alert('The time entry you are trying to approve or the entries associated with this time entry is missing a clock out time. In order to approve this time, either wait for the rep to clock out, fill the time in manually, or delete the incomplete time.');
                return;
            }
        }
        setTimeEntryApproved(timeEntry, timeEntry.Approved);
        const approved = approveTimeEntry(timeEntry, timeEntry.Approved);
        // Update group approve all visibility.
        const group = vm.timeEntries.filter(function (group) {
            return !!group.TimeEntries.filter(function (entry) {
                return entry.TimeEntryID === timeEntry.TimeEntryID;
            })[0];
        })[0];
        if (group) {
            setAllPreApproved(group);
        }

        utilityService.showLoadingUntilResolved(
            utilityService.alertOnError(approved, "Approve failed"));
    }

    function getAssociatedTimeEntries(timeEntry) {
        let isParentEntry = true;
        if (timeEntry.ParentTimeEntryID) {
            isParentEntry = false;
        }
        const employeeTimeEntries = vm.timeEntries.filter(function (entry) {
            return entry.EmployeeID === timeEntry.EmployeeID
        }).map(function (employee) {
            return employee.TimeEntries.filter(function (entry) {
                return timeEntry.TimeEntryID === entry.TimeEntryID
                    || isParentEntry && entry.ParentTimeEntryID === timeEntry.TimeEntryID
                    || !isParentEntry && (entry.TimeEntryID === timeEntry.ParentTimeEntryID
                        || timeEntry.ParentTimeEntryID === entry.ParentTimeEntryID)
            });
        }).reduce(function (a, b) {
            return a.concat(b);
        });
        return employeeTimeEntries;
    }

    function anyIncompletedTimeEntries(timeEntries) {
        return timeEntries.length > 0 && timeEntries.some(hasNoStartOrEndTime);
    }

    function hasNoStartOrEndTime(timeEntry) {
        return timeEntry.StartTime === null || timeEntry.StartTime === undefined
            || timeEntry.EndTime === null || timeEntry.EndTime === undefined;
    }

    function approveTimeEntry(timeEntry, isApproved) {
        return timeEntryService.patchTimeEntry(timeEntry.ParentTimeEntryID || timeEntry.TimeEntryID,
            { IsApproved: isApproved });
    }

    function approveAll(group) {
        if (vm.locked) return;
        utilityService.showLoading();

        //Get all the time entries that are not approved or locked
        const groupedTimeEntries = group.TimeEntries.filter(function (entry) {
            const syncPreventsEditing = entry.IsSynced && !vm.approver.IsSiteAdmin;
            return !entry.Locked && !entry.Approved && !syncPreventsEditing;
        });

        // Approve all parent time entries in the group and then refresh the grid.
        // Only select the time entry if all the child entries and the parent time entry have valid start and end time
        const filteredTimeEntries = groupedTimeEntries.filter(function (entry) {
            const childEntries = groupedTimeEntries.filter(function (childEntry) {
                return childEntry.ParentTimeEntryID === entry.TimeEntryID;
            });
            return !entry.ParentTimeEntryID && !anyIncompletedTimeEntries(childEntries) && !hasNoStartOrEndTime(entry);
        });

        const hasIncompleteTimeEntries = groupedTimeEntries
            .filter(p => !p.ParentTimeEntryID).length !== filteredTimeEntries.length;

        const requests = filteredTimeEntries.map(function (timeEntry) {
            return timeEntryService.patchTimeEntry(
                timeEntry.ParentTimeEntryID || timeEntry.TimeEntryID, { IsApproved: true });
        });

        $q.all(requests)
            .catch(function (ex) {
                alert("Approve failed: " + utilityService.getExceptionMessage(ex));
                // Partial update might have occurred, so reload the grid.
                return loadTimeEntryData();
            })
            .then(function () {
                if (hasIncompleteTimeEntries) {
                    alert('Warning: incomplete time was not approved. Please review the time entries for records with a missing end time.');
                }
                utilityService.hideLoading();
            });

        filteredTimeEntries.forEach(function (timeEntry) {
            setTimeEntryApproved(timeEntry, true);
        });

        setAllPreApproved(group);
    }

    function addTimeEntry() {
        $state.go(".new", {
            // Note that editEmployeeID is null to avoid exposing that it can be controlled via URL.
            // In this case, the value will come from the list's employeeID or the current user's ID instead.
            editEmployeeID: null,
            employeeName: vm.isReview ? vm.employeeName : null,
            startDate: vm.selectedPayroll?.StartDate,
            endDate: vm.selectedPayroll?.EndDate
        });
    }

    function reviewerTimeAdd(employeeId, employeeFullName) {
        if (!vm.locked) {
            $state.go(".new", {
                editEmployeeID: employeeId,
                employeeName: employeeFullName,
                startDate: vm.selectedPayroll?.StartDate,
                endDate: vm.selectedPayroll?.EndDate
            });
        }
    }

    function editTimeEntry(timeEntry) {
        $state.go(".edit", {
            // Set employeeID as null when the page is filtered by employee ID or the current user,
            // so that the URL doesn't generate with unnecessary parameters (which would expose that this is editable).
            editEmployeeID: vm.employeeId ? null : timeEntry.EmployeeID,
            employeeName: vm.isReview ? timeEntry.EmployeeFullName : null,
            timeEntryID: timeEntry.ParentTimeEntryID ?? timeEntry.TimeEntryID,
            startDate: vm.selectedPayroll?.StartDate,
            endDate: vm.selectedPayroll?.EndDate
        });
    }

    function deleteTimeEntry(employeeTimeEntry) {
        showDeleteTimeEntryModal(employeeTimeEntry);
    }

    /**
     * Set the approval status for the given time entry and all of its related
     * additional time entries in the list to approved.
     * @param {any} timeEntry
     * @param {any} approved
     */
    function setTimeEntryApproved(timeEntry, approved) {
        // Update approval status for time entries with the same parent.
        const id = timeEntry.ParentTimeEntryID || timeEntry.TimeEntryID;
        vm.timeEntries.forEach(function (group) {
            if (group.TimeEntries && group.TimeEntries.length > 0) {
                // Approve all other items with the same ID.
                group.TimeEntries.filter(function (entry) {
                    return entry.TimeEntryID === id || entry.ParentTimeEntryID === id;
                }).forEach(function (entry) {
                    entry.ApproverFullName = approved ? vm.approvingEmployee : "";
                    entry.Approved = approved;
                });
            }
        });
    }

    function showDeleteTimeEntryModal(timeEntry) {
        const parentID = timeEntry.ParentTimeEntryID || timeEntry.TimeEntryID;
        $uibModal.open({
            component: "apexDeleteTimeEntryModal",
            windowClass: 'xx-dialog pagespinner',
            size: 'sm',
            backdrop: 'static',
            resolve: {
                employeeFullName: function () {
                    return timeEntry.EmployeeFullName;
                },
                timeEntryID: function () {
                    return parentID;
                }
            }
        }).result
            .then(function () {
                showDeleting(parentID);
                defaultFilter = { PageNumber: 1, PageSize: vm.employeeId ? null : 25 };
                restoreFilters();
                return activate();
            })
            .catch(function (ex) {
                if (ex === 'cancel') {
                    return;
                }// Cancelled.
                console.error(ex);
                alert("Delete failed: " + utilityService.getExceptionMessage(ex));
            })
            .then(utilityService.hideLoading);
    }

    function showDeleting(timeEntryID) {
        const entries = vm.timeEntryPage.Entities.filter(p =>
            p.TimeEntryID === timeEntryID || p.ParentTimeEntryID === timeEntryID);
        for (const entry of entries) {
            entry.isDeleting = true;
        }
    }

    function setAllPreApproved(group) {
        group.allPreApproved = group.TimeEntries.reduce(function (all, timeEntry) {
            return all && (timeEntry.Approved || timeEntry.Locked);
        }, true);
    }

    function exportSummary(employeeID) {
        const summaryDownload = kendo.format(apexApiUrl + "/employees/{0}/timesheet.xlsx?startDate={1:yyyy-MM-dd}&endDate={2:yyyy-MM-dd}",
            employeeID, vm.selectedPayroll.StartDate, vm.selectedPayroll.EndDate);
        window.location.href = summaryDownload;
    }

    function exportPdf(employeeID) {
        const summaryDownload = kendo.format(apexApiUrl + "/employees/{0}/timesheet.pdf?startDate={1:yyyy-MM-dd}&endDate={2:yyyy-MM-dd}",
            employeeID, vm.selectedPayroll.StartDate, vm.selectedPayroll.EndDate);
        window.location.href = summaryDownload;
    }

    function exportAllSummary() {
        const params: any = {};
        if (vm.selectedPayroll) {
            params.startDate = kendo.format("{0:yyyy-MM-dd}", vm.selectedPayroll.StartDate);
            params.endDate = kendo.format("{0:yyyy-MM-dd}", vm.selectedPayroll.EndDate);
        }
        if (vm.selectedIcdCorporation) {
            params.icdCorporationID = vm.selectedIcdCorporation.Id;
        } else if (vm.employeeId) {
            params.icdCorporationID = vm.selectedPayroll.IcdCorporationID;
        }

        const summaryDownload = kendo.format(apexApiUrl + "icd-corporations/{0}/timesheet.xlsx?startDate={1:yyyy-MM-dd}&endDate={2:yyyy-MM-dd}",
            params.icdCorporationID, params.startDate, params.endDate);
        window.location.href = summaryDownload;
    }

    function onBackClicked() {
        const parameter: any = {};
        if (vm.selectedPayroll) {
            parameter.startDate = vm.selectedPayroll.StartDate;
            parameter.endDate = vm.selectedPayroll.EndDate;
            parameter.currDate = null;
        }
        if (vm.selectedIcdCorporation) {
            parameter.icdCorporationID = vm.selectedIcdCorporation.Id;
        } else if (vm.employeeId) {
            // The selected payroll is for a specific corporation.
            parameter.icdCorporationID = vm.selectedPayroll.IcdCorporationID;
        }
        $state.go("timeEntry.reviewPayPeriods", parameter);
    }

    function onChangePage() {
        utilityService.showLoadingUntilResolved(
            utilityService.alertOnError(
                loadTimeEntryData().then(function (timeEntries) {
                    vm.timeEntries = timeEntries;
                }), "Changing page failed"));

        saveFilters();
    }

    function selectDefaultIcdCorporation() {
        vm.selectedIcdCorporation = vm.icdCorporations.reduce(function (selected, icdCorporation) {
            if (selected) {
                return selected;
            }
            // Keep the current company selected if company is also in the new rotation,
            // or the corporation passed in with the parameters otherwise.
            if (vm.selectedIcdCorporation && icdCorporation.Id === vm.selectedIcdCorporation.Id
                || !vm.selectedIcdCorporation && icdCorporation.Id === vm.icdCorporationID) {
                return icdCorporation;
            }
            return selected;
        }, null) || vm.icdCorporations[0];
    }

    function reducePayrollCorporations(icdCorporations, payroll) {
        let icdCorporation = icdCorporations.filter(function (icdCorporation) {
            return icdCorporation.Id === payroll.IcdCorporationID;
        })[0];
        if (!icdCorporation) {
            icdCorporation = {
                Id: payroll.IcdCorporationID,
                ClientName: payroll.ClientName,
                Owner: payroll.Owner,
                Text: payroll.ClientName
            };
            icdCorporations.push(icdCorporation);
        }
        return icdCorporations;
    }
}
